import { type GamePageContext } from "@/features/game/state";

import { resolveGameLeague } from "@/features/game/data/index";

import { leagueTag } from "@/lib/leagues/watch/index";

import { buildHref, type GameDestinationContext } from "@softmax/web-addresses";
import {
  curatedCoworldName,
  curatedLeagueName,
} from "@softmax/web-features/coworlds";
import { slugifyLeagueName, leagueRoute } from "@softmax/web-features/leagues";
import { site } from "@softmax/web-addresses/discovery";

import type { PreviewContent } from "../content/preview-content.types";
import { previewNames } from "../data/preview-names";

type PreviewStartContent = Pick<
  PreviewContent,
  | "route"
  | "slug"
  | "coworldName"
  | "leagueName"
  | "name"
  | "command"
  | "commandHref"
  | "leagueId"
>;

export async function previewStartContent({
  signal,
  address,
  context,
}: {
  signal?: AbortSignal | undefined;
  address: string;
  context?: GamePageContext | undefined;
}): Promise<PreviewStartContent | null> {
  const resolved = await resolveGameLeague({
    signal,
    publicAddress: address,
    context: context ?? null,
  });

  if (resolved.status === "not-found") return null;

  if (resolved.status !== "ok")
    throw new Error("The game is temporarily unavailable");

  const { league } = resolved;
  const leagueDestination = leagueRoute(league);
  const route: GameDestinationContext = address.includes("/")
    ? leagueDestination
    : { game: leagueDestination.game };
  const slug = slugifyLeagueName(league.game.coworld_name ?? league.game.name);
  const coworldName =
    previewNames[slug]?.name ??
    curatedCoworldName(slug) ??
    (league.game.name?.trim() || leagueTag(league));
  const path = buildHref({ name: "game.playMarkdown", ...route });
  return {
    leagueId: league.id,
    route,
    slug: [slug, route.league].filter(Boolean).join("/"),
    coworldName,
    leagueName: curatedLeagueName(league),
    name: route.league ? curatedLeagueName(league) : coworldName,
    command: `${site.canonicalOrigin.replace("https://", "")}${path}`,
    commandHref: `${site.canonicalOrigin}${path}`,
  };
}
